import { useEffect, useState } from "react";
import { ShieldCheck, AlertTriangle, Lock, Server } from "lucide-react";
import api from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";

const STATUS_CHIP = {
  active: "bg-teal-600 text-white",
  attention: "bg-amber-500 text-white",
  inactive: "bg-red-600 text-white",
};

export const PosturePanel = () => {
  const [posture, setPosture] = useState(null);

  useEffect(() => {
    api.get("/security/posture").then((r) => setPosture(r.data));
  }, []);

  if (!posture) return <Skeleton className="h-96 w-full rounded-none" />;

  return (
    <div className="space-y-5" data-testid="posture-panel">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white border border-border px-4 py-3" data-testid="posture-masking">
          <div className="flex items-center justify-between">
            <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">PII Masking</p>
            <ShieldCheck className="h-4 w-4 text-teal-600" />
          </div>
          <p className="text-[15px] font-bold text-[var(--brand-primary)] mt-1">{posture.pii_masking_active ? "ACTIVE" : "INACTIVE"}</p>
          <p className="text-[10px] text-muted-foreground mt-0.5">all outbound LLM traffic</p>
        </div>
        <div className="bg-white border border-border px-4 py-3" data-testid="posture-encryption">
          <div className="flex items-center justify-between">
            <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Encryption at rest</p>
            <Lock className="h-4 w-4 text-teal-600" />
          </div>
          <p className="text-[15px] font-bold text-[var(--brand-primary)] mt-1">{posture.encryption_at_rest ? "ACTIVE" : "INACTIVE"}</p>
          <p className="text-[10px] text-muted-foreground mt-0.5">{posture.field_level_protected_records} field-level protected records</p>
        </div>
        <div className="col-span-2 bg-white border border-border px-4 py-3" data-testid="posture-residency">
          <div className="flex items-center justify-between">
            <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Data residency</p>
            <Server className="h-4 w-4 text-teal-600" />
          </div>
          <div className="flex items-center gap-2 mt-1">
            <p className="text-[15px] font-bold text-[var(--brand-primary)]">{posture.data_residency_region}</p>
            {posture.residency_compliant ? (
              <span className="inline-flex items-center gap-1 text-[9px] font-mono bg-teal-600 text-white px-1.5 py-0.5">
                <ShieldCheck className="h-3 w-3" /> {posture.data_residency_zone} SOVEREIGN
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-[9px] font-mono bg-amber-500 text-white px-1.5 py-0.5">
                <AlertTriangle className="h-3 w-3" /> OUTSIDE GCC
              </span>
            )}
          </div>
          <p className="text-[10px] text-muted-foreground mt-0.5">
            source: {posture.residency_source === "cloud_adapter" ? "configured cloud adapter (Cloud Settings)" : "platform default"}
          </p>
        </div>
      </div>

      <div className="bg-white border border-border" data-testid="posture-checklist">
        <div className="px-4 py-2.5 border-b border-border">
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
            QFC / Qatar-law compliance checklist
          </p>
        </div>
        <div className="divide-y divide-border">
          {posture.checklist.map((c, i) => (
            <div key={i} className="flex items-center gap-4 px-4 py-3" data-testid={`checklist-item-${i}`}>
              <span className={`text-[9px] font-mono px-2 py-1 uppercase shrink-0 ${STATUS_CHIP[c.status]}`}>{c.status}</span>
              <div className="flex-1">
                <p className="text-[13px] text-slate-800">{c.item}</p>
                <p className="text-[10.5px] font-mono text-muted-foreground mt-0.5">{c.reference}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {posture.adapters?.length > 0 && (
        <div className="bg-white border border-border px-4 py-3" data-testid="posture-adapters">
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground mb-2">
            Enterprise adapters feeding residency
          </p>
          {posture.adapters.map((a) => (
            <p key={a.id} className="text-[12px] font-mono text-slate-600 py-0.5">
              {a.provider_label} · {a.region_label} · {a.sovereign ? "GCC sovereign" : "non-GCC (acknowledged)"}
            </p>
          ))}
        </div>
      )}
    </div>
  );
};

export default PosturePanel;
